/**
 * Edits an inline-loop chip: the collection (with any loop formatters), the item name and the repeated text as
 * template text. Opened by clicking a chip, or Enter on a selected one (cdm/loopRunEdit.ts); anchored to the chip like
 * ConditionalRunPopover. Apply writes one transaction; Esc, Cancel or a click outside discards.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react'
import type { Editor } from '@tiptap/core'
import { colors, fontSize, radius, shadow, spacing } from '../../styles/theme'
import { branchTextError, branchToText } from '../../cdm/conditionalRunEdit'
import { applyLoopRunEdit, DEFAULT_ITEM_ALIAS, itemAliasError, loopSourceError, setLoopRunEditHandler, type LoopRunEdit } from '../../cdm/loopRunEdit'
import type { LoopRun } from '../../cdm/types'

const POPOVER_WIDTH = 360

function toDraft(run: LoopRun): LoopRunEdit {
  return { rawName: run.rawName, itemAlias: run.itemAlias || DEFAULT_ITEM_ALIAS, text: branchToText(run.inlines) }
}

export default function LoopRunPopover({ editor }: { editor: Editor | null }) {
  const [pos, setPos] = useState<number | null>(null)
  const [draft, setDraft] = useState<LoopRunEdit | null>(null)
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null)
  const boxRef = useRef<HTMLFormElement>(null)
  const nameRef = useRef<HTMLInputElement>(null)

  const close = useCallback((refocus: boolean) => {
    setPos(null)
    setDraft(null)
    setAnchor(null)
    if (refocus) editor?.view.focus()
  }, [editor])

  useEffect(() => {
    if (!editor) return
    setLoopRunEditHandler(editor, p => {
      const node = editor.state.doc.nodeAt(p)
      if (!node || node.type.name !== 'loopRun' || !node.attrs.json) return
      setDraft(toDraft(node.attrs.json as LoopRun))
      setPos(p)
    })
    return () => setLoopRunEditHandler(editor, null)
  }, [editor])

  const reposition = useCallback(() => {
    if (!editor || pos === null) return
    const dom = editor.view.nodeDOM(pos)
    if (!(dom instanceof HTMLElement)) return
    const r = dom.getBoundingClientRect()
    const left = Math.max(8, Math.min(r.left, window.innerWidth - POPOVER_WIDTH - 8))
    const h = boxRef.current?.offsetHeight ?? 0
    const below = r.bottom + 6
    const top = below + h > window.innerHeight - 8 && r.top - 6 - h >= 8 ? r.top - 6 - h : below
    setAnchor({ top, left })
  }, [editor, pos])

  useLayoutEffect(() => {
    if (pos === null) return
    reposition()
    const raf = window.requestAnimationFrame(reposition)
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.cancelAnimationFrame(raf)
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [pos, reposition])

  useEffect(() => {
    if (pos === null) return
    nameRef.current?.focus()
    nameRef.current?.select()
    const onDown = (e: MouseEvent) => { if (!boxRef.current?.contains(e.target as Node)) close(false) }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [pos, close])

  useEffect(() => {
    if (!editor || pos === null) return
    const check = () => { if (editor.state.doc.nodeAt(pos)?.type.name !== 'loopRun') close(false) }
    editor.on('update', check)
    return () => { editor.off('update', check) }
  }, [editor, pos, close])

  if (!editor || pos === null || !draft) return null

  const sourceError = loopSourceError(draft.rawName)
  const aliasError = itemAliasError(draft.itemAlias)
  const textError = draft.text !== null ? branchTextError(draft.text) : null
  const invalid = sourceError !== null || aliasError !== null || textError !== null
  const set = (patch: Partial<LoopRunEdit>) => setDraft(d => (d ? { ...d, ...patch } : d))
  const alias = draft.itemAlias.trim() || DEFAULT_ITEM_ALIAS

  function submit(e: FormEvent) {
    e.preventDefault()
    if (invalid || pos === null || !editor || !draft) return
    applyLoopRunEdit(editor, pos, draft)
    close(true)
  }

  const input: CSSProperties = { padding: '4px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, minWidth: 0, boxSizing: 'border-box', fontFamily: 'monospace' }
  const label: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, color: colors.textSecondary }
  const error = (msg: string | null) => msg && <div role="alert" style={{ color: colors.error, fontSize: fontSize.xs }}>{msg}</div>

  return (
    <form
      ref={boxRef}
      role="dialog"
      aria-label="Edit inline loop"
      onSubmit={submit}
      onKeyDown={e => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(true) } }}
      style={{
        position: 'fixed', top: anchor?.top ?? 0, left: anchor?.left ?? 0, width: POPOVER_WIDTH, zIndex: 30,
        visibility: anchor ? 'visible' : 'hidden', padding: spacing[3], background: colors.surface,
        border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.lg, fontSize: fontSize.sm, color: colors.text,
        display: 'flex', flexDirection: 'column', gap: spacing.sm, boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'flex-end' }}>
        <label style={{ ...label, flex: 1 }}>
          Repeat for each item of
          <input ref={nameRef} value={draft.rawName} onChange={e => set({ rawName: e.target.value })} spellCheck={false}
            aria-label="Collection" aria-invalid={sourceError !== null} style={input} />
        </label>
        <label style={{ ...label, flex: '0 0 96px' }}>
          Item name
          <input value={draft.itemAlias} onChange={e => set({ itemAlias: e.target.value })} spellCheck={false}
            aria-label="Item name" aria-invalid={aliasError !== null} placeholder={DEFAULT_ITEM_ALIAS} style={input} />
        </label>
      </div>
      {draft.rawName !== '' && error(sourceError)}
      {error(aliasError)}

      <label style={label}>
        Repeated text
        {draft.text === null ? (
          <div style={{ padding: '4px 8px', background: colors.background, borderRadius: radius.sm, color: colors.textSecondary, fontSize: fontSize.xs }}>
            Holds content that can only be changed in the template source (a nested condition, loop or link). Kept as it is.
          </div>
        ) : (
          <textarea aria-label="Repeated text" value={draft.text} onChange={e => set({ text: e.target.value })} spellCheck={false} rows={2}
            style={{ ...input, resize: 'vertical', minHeight: 48 }} />
        )}
      </label>
      {error(textError)}

      <div style={{ color: colors.textMuted, fontSize: fontSize.xs }}>
        Fields resolve on each item: {'{{name}}'} or {`{{${alias}.name}}`}; {'{{$index}}'} counts from 0. Sort with {'items | sort:price'}.
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: spacing.sm }}>
        <button type="button" onClick={() => close(true)} style={{ padding: '4px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: 'none', color: colors.textSecondary, cursor: 'pointer', fontSize: fontSize.sm }}>Cancel</button>
        <button type="submit" disabled={invalid} style={{ padding: '4px 12px', border: 'none', borderRadius: radius.sm, background: invalid ? colors.border : colors.primary, color: '#fff', cursor: invalid ? 'default' : 'pointer', fontSize: fontSize.sm, fontWeight: 600 }}>Apply</button>
      </div>
    </form>
  )
}
